const _Editor_lintingSuspended = Symbol("#lintingSuspended"),
      _Editor_hadChangesSinceLastLinting = Symbol("#hadChangesSinceLastLinting"),
      _Editor_changePending = Symbol("#changePending"),
      _Editor_changeReason = Symbol("#changeReason"),
      _Editor_changesAreFromServer = Symbol("#changesAreFromServer"),
      _Editor_onConnectionOpen = Symbol("#onConnectionOpen"),
      _Editor_hideConnectionLoss = Symbol("#hideConnectionLoss"),
      _Editor_hasDefaultServerOptions = Symbol("#hasDefaultServerOptions"),
      _Editor_connection = Symbol("#connection"),
      _Editor_pendingServerOptions = Symbol("#pendingServerOptions"),
      _Editor_serverOptions = Symbol("#serverOptions"),
      _Editor_cm = Symbol("#cm"),
      _Editor_getCursorIndex = Symbol("#getCursorIndex"),
      _Editor_options = Symbol("#options"),
      _Editor_capturedUpdateLinting = Symbol("#capturedUpdateLinting"),
      _Editor_requestSlowUpdate = Symbol("#requestSlowUpdate"),
      _Editor_onConnectionMessage = Symbol("#onConnectionMessage"),
      _Editor_receiveServerChanges = Symbol("#receiveServerChanges"),
      _Editor_hinter = Symbol("#hinter"),
      _Editor_signatureTip = Symbol("#signatureTip"),
      _Editor_spanToRange = Symbol("#spanToRange"),
      _Editor_showSlowUpdate = Symbol("#showSlowUpdate"),
      _Editor_receiveServerOptions = Symbol("#receiveServerOptions"),
      _Editor_selfDebug = Symbol("#selfDebug"),
      _Editor_onConnectionCloseOrError = Symbol("#onConnectionCloseOrError"),
      _Editor_showConnectionLoss = Symbol("#showConnectionLoss"),
      _Editor_onCodeMirrorBeforeChange = Symbol("#onCodeMirrorBeforeChange"),
      _Editor_onCodeMirrorCursorActivity = Symbol("#onCodeMirrorCursorActivity"),
      _Editor_onCodeMirrorChanges = Symbol("#onCodeMirrorChanges"),
      _Editor_mergeChanges = Symbol("#mergeChanges"),
      _Editor_getText = Symbol("#getText"),
      _Editor_lintGetAnnotations = Symbol("#lintGetAnnotations"),
      _Editor_getLintFixes = Symbol("#getLintFixes"),
      _Editor_infotipGetInfo = Symbol("#infotipGetInfo"),
      _Editor_connectionLossElement = Symbol("#connectionLossElement"),
      _Editor_sendServerOptions = Symbol("#sendServerOptions"),
      _Editor_language = Symbol("#language"),
      _Editor_keyMap = Symbol("#keyMap"),
      _Editor_removeConnectionEvents = Symbol("#removeConnectionEvents"),
      _Editor_removeCodeMirrorEvents = Symbol("#removeCodeMirrorEvents");

import CodeMirror from 'codemirror';
import 'codemirror/mode/clike/clike.js';
import 'codemirror-addon-infotip';
import 'codemirror-addon-lint-fix';
import { renderInfotip } from "./render-infotip.js";
import { Hinter } from "./hinter.js";
import { SignatureTip } from "./signature-tip.js";
import { addEvents } from "../helpers/add-events.js";
const indexKey = '$mirrorsharp-index';
const languageModes = {
  'C#': 'text/x-csharp',
  'Visual Basic': 'text/x-vb',
  'F#': 'text/x-fsharp',
  'IL': 'text/x-cil',
  'PHP': 'application/x-httpd-php'
};
const defaultLanguage = 'C#';
const lineSeparator = '\r\n';
export class Editor {
  constructor(textarea, connection, selfDebug, options) {
    this[_Editor_lintingSuspended] = true;
    this[_Editor_hadChangesSinceLastLinting] = false;
    this[_Editor_changePending] = false;
    this[_Editor_changeReason] = null;
    this[_Editor_changesAreFromServer] = false;

    this[_Editor_onConnectionOpen] = e => {
      this[_Editor_hideConnectionLoss]();

      if (!this[_Editor_hasDefaultServerOptions]()) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this[_Editor_connection].sendSetOptions(this[_Editor_pendingServerOptions] ?? this[_Editor_serverOptions]);
      }

      const text = this[_Editor_cm].getValue();

      if (text === '' || text == null) {
        this[_Editor_lintingSuspended] = false;
        return;
      } // eslint-disable-next-line @typescript-eslint/no-floating-promises


      this[_Editor_connection].sendReplaceText(0, 0, text, this[_Editor_getCursorIndex]()); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


      this[_Editor_options].on.connectionChange('open', e);

      this[_Editor_lintingSuspended] = false;
      this[_Editor_hadChangesSinceLastLinting] = true;

      if (this[_Editor_capturedUpdateLinting]) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this[_Editor_requestSlowUpdate]();
      }
    };

    this[_Editor_onConnectionMessage] = message => {
      switch (message.type) {
        case 'changes':
          this[_Editor_receiveServerChanges](message.changes, message.reason);

          break;

        case 'completions':
          this[_Editor_hinter].start(message.completions, message.span, {
            commitChars: message.commitChars,
            suggestion: message.suggestion
          });

          break;

        case 'completionInfo':
          this[_Editor_hinter].showTip(message.index, message.parts);

          break;

        case 'signatures':
          this[_Editor_signatureTip].update(message);

          break;

        case 'infotip':
          if (!message.sections) {
            this[_Editor_cm].infotipUpdate(null);

            return;
          }

          this[_Editor_cm].infotipUpdate({
            data: message,
            range: this[_Editor_spanToRange](message.span)
          });

          break;

        case 'slowUpdate':
          this[_Editor_showSlowUpdate](message);

          break;

        case 'optionsEcho':
          this[_Editor_receiveServerOptions](message.options);

          break;

        case 'self:debug':
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this[_Editor_selfDebug].displayData(message);

          break;

        case 'error':
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this[_Editor_options].on.serverError(message.message);

          break;

        default:
          throw new Error('Unknown message type "' + message.type);
      }
    };

    this[_Editor_onConnectionCloseOrError] = e => {
      this[_Editor_lintingSuspended] = true;

      this[_Editor_showConnectionLoss](); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


      const connectionChange = this[_Editor_options].on.connectionChange;

      if (e instanceof CloseEvent) {
        connectionChange('close', e);
      } else {
        connectionChange('error', e);
      }
    };

    this[_Editor_onCodeMirrorBeforeChange] = (_, change) => {
      change.from[indexKey] = this[_Editor_cm].indexFromPos(change.from);
      change.to[indexKey] = this[_Editor_cm].indexFromPos(change.to);
      this[_Editor_changePending] = true;
    };

    this[_Editor_onCodeMirrorCursorActivity] = () => {
      if (this[_Editor_changePending]) return; // eslint-disable-next-line @typescript-eslint/no-floating-promises

      this[_Editor_connection].sendMoveCursor(this[_Editor_getCursorIndex]());
    };

    this[_Editor_onCodeMirrorChanges] = (_, changes) => {
      this[_Editor_hadChangesSinceLastLinting] = true;
      this[_Editor_changePending] = false;

      const cursorIndex = this[_Editor_getCursorIndex]();

      changes = this[_Editor_mergeChanges](changes);

      for (let i = 0; i < changes.length; i++) {
        const change = changes[i];
        const start = change.from[indexKey];
        const length = change.to[indexKey] - start;
        const text = change.text.join(lineSeparator);

        if (cursorIndex === start + 1 && text.length === 1 && !this[_Editor_changesAreFromServer]) {
          if (length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this[_Editor_connection].sendReplaceText(start, length, '', cursorIndex - 1);
          } // eslint-disable-next-line @typescript-eslint/no-floating-promises


          this[_Editor_connection].sendTypeChar(text);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this[_Editor_connection].sendReplaceText(start, length, text, cursorIndex, this[_Editor_changeReason]);
        }
      } // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


      this[_Editor_options].on.textChange(this[_Editor_getText]);
    };

    this[_Editor_mergeChanges] = changes => {
      if (changes.length < 2) return changes;

      const canBeMerged = (first, second) => {
        return first && second && first.origin === 'undo' && second.origin === 'undo' && first.to.line === second.from.line && first.text.length === 1 && second.text.length === 1 && second.from.ch === second.to.ch && first.to.ch + first.text[0].length === second.from.ch;
      };

      const results = [];
      let before = null;

      for (const change of changes) {
        if (canBeMerged(before, change)) {
          before = {
            /* eslint-disable @typescript-eslint/no-non-null-assertion */
            from: before.from,
            to: before.to,
            text: [before.text[0] + change.text[0]],
            origin: change.origin
            /* eslint-enable @typescript-eslint/no-non-null-assertion */

          };
        } else {
          if (before) results.push(before);
          before = change;
        }
      } // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


      results.push(before);
      return results;
    };

    this[_Editor_lintGetAnnotations] = (_, updateLinting) => {
      if (!this[_Editor_capturedUpdateLinting]) {
        this[_Editor_capturedUpdateLinting] = function (...args) {
          const [cm] = args; // see https://github.com/codemirror/CodeMirror/blob/dbaf6a94f1ae50d387fa77893cf6b886988c2147/addon/lint/lint.js#L133
          // ensures that next 'id' will always match 'waitingFor'

          cm.state.lint.waitingFor = -1;
          updateLinting.apply(this, args);
        };
      } // eslint-disable-next-line @typescript-eslint/no-floating-promises


      this[_Editor_requestSlowUpdate]();
    };

    this[_Editor_getText] = () => this[_Editor_cm].getValue();

    this[_Editor_getCursorIndex] = () => this[_Editor_cm].indexFromPos(this[_Editor_cm].getCursor());

    this[_Editor_receiveServerChanges] = (changes, reason) => {
      this[_Editor_changesAreFromServer] = true;
      this[_Editor_changeReason] = reason ?? 'server';

      this[_Editor_cm].operation(() => {
        let offset = 0;

        for (const change of changes) {
          const from = this[_Editor_cm].posFromIndex(change.start + offset);

          const to = change.length > 0 ? this[_Editor_cm].posFromIndex(change.start + offset + change.length) : from;

          this[_Editor_cm].replaceRange(change.text, from, to, '+server');

          offset += change.text.length - change.length;
        }
      });

      this[_Editor_changeReason] = null;
      this[_Editor_changesAreFromServer] = false;
    };

    this[_Editor_getLintFixes] = (cm, line, annotations) => {
      const requestApplyFix = (cm, line, fix) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this[_Editor_connection].sendApplyDiagnosticAction(fix.id);
      };

      const fixes = [];

      for (const annotation of annotations) {
        const diagnostic = annotation.diagnostic;
        if (!diagnostic.actions) continue;

        for (const action of diagnostic.actions) {
          fixes.push({
            text: action.title,
            apply: requestApplyFix,
            id: action.id
          });
        }
      }

      return fixes;
    };

    this[_Editor_infotipGetInfo] = (cm, position) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this[_Editor_connection].sendRequestInfoTip(cm.indexFromPos(position));
    };

    this[_Editor_requestSlowUpdate] = force => {
      if (this[_Editor_lintingSuspended] || !(this[_Editor_hadChangesSinceLastLinting] || force)) return null;
      this[_Editor_hadChangesSinceLastLinting] = false; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

      this[_Editor_options].on.slowUpdateWait();

      return this[_Editor_connection].sendSlowUpdate();
    };

    this[_Editor_showSlowUpdate] = update => {
      const annotations = []; // Higher severities must go last -- CodeMirror uses last one for the icon.
      // Unless one is error, in which case it's always error -- but still makes
      // sense to handle this consistently.

      const priorityBySeverity = {
        hidden: 0,
        info: 1,
        warning: 2,
        error: 3
      };
      const diagnostics = update.diagnostics.slice(0);
      diagnostics.sort((a, b) => {
        const aOrder = priorityBySeverity[a.severity];
        const bOrder = priorityBySeverity[b.severity];
        return aOrder !== bOrder ? aOrder > bOrder ? 1 : -1 : 0;
      });

      for (const diagnostic of diagnostics) {
        let severity = diagnostic.severity;
        const isUnnecessary = diagnostic.tags.includes('unnecessary');
        if (severity === 'hidden' && !isUnnecessary) continue;
        if (isUnnecessary && (severity === 'hidden' || severity === 'info')) severity = 'unnecessary';

        const range = this[_Editor_spanToRange](diagnostic.span);

        annotations.push({
          severity,
          message: diagnostic.message,
          from: range.from,
          to: range.to,
          diagnostic
        });
      } // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


      this[_Editor_capturedUpdateLinting](this[_Editor_cm], annotations); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


      this[_Editor_options].on.slowUpdateResult({
        diagnostics: update.diagnostics,
        x: update.x
      });
    };

    this[_Editor_showConnectionLoss] = () => {
      const wrapper = this[_Editor_cm].getWrapperElement();

      if (!this[_Editor_connectionLossElement]) {
        const connectionLossElement = document.createElement('div');
        connectionLossElement.setAttribute('class', 'mirrorsharp-connection-issue');
        connectionLossElement.innerText = 'Server connection lost, reconnecting…';
        wrapper.appendChild(connectionLossElement);
        this[_Editor_connectionLossElement] = connectionLossElement;
      }

      wrapper.classList.add('mirrorsharp-connection-has-issue');
    };

    this[_Editor_hideConnectionLoss] = () => {
      this[_Editor_cm].getWrapperElement().classList.remove('mirrorsharp-connection-has-issue');
    };

    this[_Editor_hasDefaultServerOptions] = () => {
      const keys = Object.keys(this[_Editor_pendingServerOptions] ?? this[_Editor_serverOptions]);
      return keys.length === 1 && keys[0] === 'language' && this[_Editor_serverOptions].language === defaultLanguage;
    };

    this[_Editor_sendServerOptions] = async value => {
      this[_Editor_pendingServerOptions] = { ...this[_Editor_serverOptions],
        ...value
      };
      await this[_Editor_connection].sendSetOptions(value);
      await this[_Editor_requestSlowUpdate](true);
    };

    this[_Editor_receiveServerOptions] = value => {
      this[_Editor_pendingServerOptions] = null;
      this[_Editor_serverOptions] = { ...this[_Editor_serverOptions],
        ...value
      }; // TODO: understand later
      // eslint-disable-next-line no-undefined

      if (value.language !== undefined && value.language !== this[_Editor_language]) {
        this[_Editor_language] = value.language;

        this[_Editor_cm].setOption('mode', languageModes[this[_Editor_language]]);
      }
    };

    this[_Editor_spanToRange] = span => {
      return {
        from: this[_Editor_cm].posFromIndex(span.start),
        to: this[_Editor_cm].posFromIndex(span.start + span.length)
      };
    };

    this[_Editor_connection] = connection;
    this[_Editor_selfDebug] = selfDebug;
    options = {
      language: defaultLanguage,
      ...options
    };
    options.on = {
      slowUpdateWait: () => ({}),
      slowUpdateResult: () => ({}),
      textChange: () => ({}),
      connectionChange: () => ({}),
      serverError: message => {
        throw new Error(message);
      },
      ...options.on
    };
    this[_Editor_options] = options;
    const cmOptions = {
      gutters: [],
      indentUnit: 4,
      ...options.forCodeMirror,
      lineSeparator,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      mode: languageModes[options.language],
      lint: {
        async: true,
        getAnnotations: this[_Editor_lintGetAnnotations],
        hasGutters: true
      },
      lintFix: {
        getFixes: this[_Editor_getLintFixes]
      },
      infotip: {
        async: true,
        delay: 500,
        getInfo: this[_Editor_infotipGetInfo],
        render: renderInfotip
      }
    }; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    cmOptions.gutters.push('CodeMirror-lint-markers'); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    this[_Editor_language] = options.language;
    this[_Editor_serverOptions] = { ...(options.initialServerOptions ?? {}),
      language: this[_Editor_language]
    };

    const cmSource = function getCodeMirror() {
      const next = textarea.nextSibling;

      if (next?.CodeMirror) {
        const existing = next.CodeMirror;

        for (const key in cmOptions) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
          existing.setOption(key, cmOptions[key]);
        }

        return {
          cm: existing,
          existing: true
        };
      }

      return {
        cm: CodeMirror.fromTextArea(textarea, cmOptions)
      };
    }();

    this[_Editor_cm] = cmSource.cm;
    this[_Editor_keyMap] = {
      /* eslint-disable object-shorthand */
      'Tab': () => {
        if (this[_Editor_cm].somethingSelected()) {
          this[_Editor_cm].execCommand('indentMore');

          return;
        }

        this[_Editor_cm].replaceSelection('    ', 'end');
      },
      'Shift-Tab': 'indentLess',
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      'Ctrl-Space': () => {
        connection.sendCompletionState('force');
      },
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      'Shift-Ctrl-Space': () => {
        connection.sendSignatureHelpState('force');
      },
      'Ctrl-.': 'lintFixShow',
      'Shift-Ctrl-Y': selfDebug ? () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        selfDebug.requestData(connection);
      } : false
      /* eslint-enable object-shorthand */

    };

    this[_Editor_cm].addKeyMap(this[_Editor_keyMap]); // see https://github.com/codemirror/CodeMirror/blob/dbaf6a94f1ae50d387fa77893cf6b886988c2147/addon/lint/lint.js#L133
    // ensures that next 'id' will be -1 whether a change happened or not
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access


    this[_Editor_cm].state.lint.waitingFor = -2;
    if (!cmSource.existing) this.setText(textarea.value);

    const getText = this[_Editor_cm].getValue.bind(this[_Editor_cm]);

    if (selfDebug) selfDebug.watchEditor(getText, this[_Editor_getCursorIndex]);

    const cmWrapper = this[_Editor_cm].getWrapperElement();

    cmWrapper.classList.add('mirrorsharp', 'mirrorsharp-theme');
    this[_Editor_hinter] = new Hinter(this[_Editor_cm], connection);
    this[_Editor_signatureTip] = new SignatureTip(this[_Editor_cm]);
    this[_Editor_removeConnectionEvents] = addEvents(connection, {
      open: this[_Editor_onConnectionOpen],
      message: this[_Editor_onConnectionMessage],
      error: this[_Editor_onConnectionCloseOrError],
      close: this[_Editor_onConnectionCloseOrError]
    });
    this[_Editor_removeCodeMirrorEvents] = addEvents(this[_Editor_cm], {
      beforeChange: this[_Editor_onCodeMirrorBeforeChange],
      cursorActivity: this[_Editor_onCodeMirrorCursorActivity],
      changes: this[_Editor_onCodeMirrorChanges]
    });
  }

  getCodeMirror() {
    return this[_Editor_cm];
  }

  setText(text) {
    this[_Editor_cm].setValue(text.replace(/(\r\n|\r|\n)/g, '\r\n'));
  }

  getLanguage() {
    return this[_Editor_language];
  }

  setLanguage(value) {
    return this[_Editor_sendServerOptions]({
      language: value
    });
  }

  setServerOptions(value) {
    return this[_Editor_sendServerOptions](value);
  }

  destroy(destroyOptions = {}) {
    this[_Editor_cm].save();

    this[_Editor_removeConnectionEvents]();

    if (!destroyOptions.keepCodeMirror) {
      this[_Editor_cm].toTextArea();

      return;
    }

    this[_Editor_cm].removeKeyMap(this[_Editor_keyMap]);

    this[_Editor_removeCodeMirrorEvents](); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


    this[_Editor_cm].setOption('lint', null);

    this[_Editor_cm].setOption('lintFix', null); // TODO: fix in infotip
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion


    this[_Editor_cm].setOption('infotip', null);
  }

}